import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import NumberFormat from "react-number-format";
import {CheckIcon, TrashIcon} from "@heroicons/react/outline";

import SlidePanel from "../../../components/layout/slide-panel";
import ListSection from "../../../components/layout/list-section";
import DynamicFilterMenu from "../../../components/common/table/dynamic-filter-menu";
import ProductCell from "../../../components/common/table/product-cell";
import TableDetail from "../../../components/common/list/table-detail";
import CardDetail from "../../../components/common/list/card-detail";
import {StatusBarIcon} from "../../../components/common/alert/status-bar-icon";
import InputFormattedNumber from "../../../components/common/form/input-formatted-number";
import InputDiscountNumber from "../../../components/common/form/input-discount-number";
import PriceHistory from "../../../components/common/card/price-history";
import {generateParamsFormLocation} from "../../../constants/dynamic-filter";
import {getProductsFilter} from "../../../api/catalog";
import {getQuerySearchParam} from "../../../utils/converter";
import {
    DOCUMENT_PANEL_OVERLAY_Z_INDEX_CLASS,
    DOCUMENT_PANEL_Z_INDEX_CLASS,
} from "../constants";

const DEFAULT_LIMIT = 25;
const normalizeDiscountValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    }
    if (typeof value === "object") {
        const candidates = [value.value, value.price, value.discount];
        for (const candidate of candidates) {
            if (candidate === null || candidate === undefined) {
                continue;
            }
            const numeric = Number(candidate);
            if (Number.isFinite(numeric)) {
                return numeric;
            }
        }
    }
    return null;
};

const extractDiscounts = (product) => {
    const discountSource = product?.pricing?.discount ?? product?.discount ?? [];
    if (!Array.isArray(discountSource)) {
        return [];
    }
    return discountSource
        .map(normalizeDiscountValue)
        .filter((value) => value !== null);
};

const resolveCurrencySymbol = (product, fallbackSymbol) => {
    const symbol = product?.currency_symbol
        ?? product?.currency?.symbol
        ?? product?.company_currency?.symbol
        ?? fallbackSymbol
        ?? "";
    return typeof symbol === "string" ? symbol.trim() : "";
};

const EMPTY_SET = new Set();

const resolveProductIdentifier = (product) => {
    if (!product || typeof product !== "object") {
        return null;
    }
    const candidates = [
        product.id,
        product.product_id,
        product.product_reference,
        product.reference,
        product.code,
        product.sku,
        product.uuid,
    ];
    for (const candidate of candidates) {
        if (candidate === null || candidate === undefined) {
            continue;
        }
        if (typeof candidate === "string" && candidate.trim() === "") {
            continue;
        }
        return String(candidate);
    }
    return null;
};

const buildConfigFromProduct = (product, currencySymbol) => {
    if (!product) {
        return null;
    }

    const resolvedListPrice = (() => {
        const candidate = product?.pricing?.list_price ?? product?.list_price;
        const numeric = Number(candidate);
        return Number.isFinite(numeric) ? numeric : 0;
    })();

    const resolvedUnitPrice = (() => {
        const candidate = product?.pricing?.final_price
            ?? product?.pricing?.sale_price
            ?? product?.pricing?.net_price
            ?? product?.net_price
            ?? product?.price
            ?? resolvedListPrice;
        const numeric = Number(candidate);
        return Number.isFinite(numeric) ? numeric : resolvedListPrice;
    })();

    const quantityDefault = 1;

    const derivedId = String(product?.id ?? product?.product_reference ?? Date.now());

    return {
        id: derivedId,
        cacheKey: derivedId,
        product,
        name: product?.name ?? "",
        description: product?.description ?? "",
        product_reference: product?.product_reference ?? "",
        quantity: quantityDefault,
        unitBasePrice: resolvedUnitPrice,
        unitPrice: resolvedUnitPrice,
        listPrice: resolvedListPrice,
        listPriceDiscounts: extractDiscounts(product),
        unitPriceDiscounts: [],
        currencySymbol: resolveCurrencySymbol(product, currencySymbol),
        unitQuantityId: product?.unit_value?.id ?? product?.unit_quantity?.id ?? null,
    };
};

const normalizePositiveNumber = (value, fallback) => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return fallback;
    }
    if (numeric < 0) {
        return fallback;
    }
    return numeric;
};

const mapConfigToPayload = (config) => {
    if (!config || !config.product) {
        return null;
    }

    const quantity = normalizePositiveNumber(config.quantity, 1);
    const unitBasePrice = normalizePositiveNumber(
        config.unitBasePrice,
        normalizePositiveNumber(config.unitPrice, 0)
    );
    const unitPrice = normalizePositiveNumber(
        config.unitPrice,
        unitBasePrice
    );
    const listPrice = normalizePositiveNumber(
        config.listPrice,
        unitPrice
    );

    const payload = {
        name: (config.name ?? "").trim() || (config.product?.name ?? ""),
        description: config.description ?? "",
        quantity,
        unit_price: unitBasePrice,
        net_price: unitPrice,
        total_net_price: unitPrice * quantity,
        product_reference: config.product_reference ?? "",
        editable: true,
        list_price: listPrice,
        list_price_discounts: Array.isArray(config.listPriceDiscounts)
            ? config.listPriceDiscounts
                .map((entry) => Number(entry))
                .filter((entry) => Number.isFinite(entry))
            : [],
        unit_price_discounts: Array.isArray(config.unitPriceDiscounts)
            ? config.unitPriceDiscounts
                .map((entry) => Number(entry))
                .filter((entry) => Number.isFinite(entry))
            : [],
    };

    if (config.product?.id) {
        payload.product = {id: config.product.id};
    }

    if (config.unitQuantityId) {
        payload.unit_quantity = {id: config.unitQuantityId};
    }

    if (config.product?.pricing?.purchase_price !== undefined && config.product?.pricing?.purchase_price !== null) {
        const purchasePrice = Number(config.product.pricing.purchase_price);
        if (Number.isFinite(purchasePrice)) {
            payload.purchase_price = purchasePrice;
        }
    }

    return payload;
};

const MOBILE_SECTIONS = {
    SEARCH: "search",
    CONFIG: "config",
};

const DocumentBodyCatalogPanel = ({
    isOpen,
    onClose,
    onConfirm,
    companyId,
    isSubmitting,
    currencySymbol,
}) => {
    const {t} = useTranslation();
    const location = useLocation();
    const {company} = useSelector((state) => state.userCompany ?? {});

    const [query, setQuery] = useState(getQuerySearchParam());
    const [offset, setOffset] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [dynamicFilters, setDynamicFilters] = useState([]);
    const [activeDynamicFilter, setActiveDynamicFilter] = useState(() => generateParamsFormLocation(location.search));
    const [defaultParamsUsed, setDefaultParamsUsed] = useState(false);
    const [defaultParams, setDefaultParams] = useState("");
    const [selectedConfigs, setSelectedConfigs] = useState([]);
    const [mobileSection, setMobileSection] = useState(MOBILE_SECTIONS.SEARCH);
    const computePanelHeight = () => {
        if (typeof window === "undefined") {
            return 720;
        }
        return Math.max(window.innerHeight - 260, 360);
    };
    const computeIsDesktopLayout = () => {
        if (typeof window === "undefined") {
            return false;
        }
        return window.innerWidth >= 1280;
    };
    const [panelScrollHeight, setPanelScrollHeight] = useState(computePanelHeight);
    const [isDesktopLayout, setIsDesktopLayout] = useState(computeIsDesktopLayout);
    const initializedPanelRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            initializedPanelRef.current = false;
            return undefined;
        }
        const isFirstOpen = initializedPanelRef.current === false;
        initializedPanelRef.current = true;
        setQuery(getQuerySearchParam());
        setOffset(0);
        setProducts([]);
        setTotalRows(0);
        setDynamicFilters([]);
        setActiveDynamicFilter(generateParamsFormLocation(location.search));
        setDefaultParams("");
        setDefaultParamsUsed(false);
        if (isFirstOpen) {
            setSelectedConfigs([]);
        }
        setMobileSection(MOBILE_SECTIONS.SEARCH);
        return undefined;
    }, [isOpen, location.search]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }
        const handleResize = () => {
            setPanelScrollHeight(computePanelHeight());
            setIsDesktopLayout(computeIsDesktopLayout());
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (!isOpen || !companyId) {
            return undefined;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await getProductsFilter({
                    company_id: companyId,
                    params: {
                        limit: DEFAULT_LIMIT,
                        offset: query.length > 0 ? 0 : offset,
                        query,
                    },
                    data: activeDynamicFilter,
                    signal,
                });

                if (response?.filters) {
                    setDynamicFilters(response.filters);
                }

                if (
                    response?.default_filter &&
                    !defaultParamsUsed &&
                    (!Array.isArray(activeDynamicFilter) || activeDynamicFilter.length === 0)
                ) {
                    setDefaultParams(`?${response.default_filter}`);
                    setDefaultParamsUsed(true);
                }

                const nextProducts = Array.isArray(response?.products) ? response.products : [];
                if (query.length > 0 || offset === 0) {
                    setProducts(nextProducts);
                    setTotalRows(response?.total_rows ?? nextProducts.length);
                } else {
                    setProducts((previous) => {
                        const previousList = Array.isArray(previous) ? previous : [];
                        const merged = [...previousList, ...nextProducts];
                        return merged;
                    });
                    setTotalRows(response?.total_rows ?? nextProducts.length);
                }
            } catch (error) {
                if (!signal.aborted) {
                    setProducts([]);
                    setTotalRows(0);
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchProducts();

        return () => {
            controller.abort();
        };
    }, [activeDynamicFilter, companyId, defaultParamsUsed, isOpen, offset, query]);

    const visibleSelectedConfigs = useMemo(
        () => selectedConfigs.filter((config) => Boolean(config?.product)),
        [selectedConfigs]
    );

    const selectedIdSet = useMemo(() => new Set(
        visibleSelectedConfigs
            .map((config) => resolveProductIdentifier(config?.product))
            .filter((value) => value !== null)
    ), [visibleSelectedConfigs]);

    const selectedProductRefSet = useMemo(() => new Set(
        visibleSelectedConfigs
            .map((config) => config?.product)
            .filter((product) => product !== null && product !== undefined)
    ), [visibleSelectedConfigs]);

    const columns = useMemo(() => ([
        t("app.container.product"),
        t("app.products.manufacture_code"),
        (company?.product_manage_status ?? false) && t("app.products.status"),
    ].filter(Boolean)), [company?.product_manage_status, t]);
    const selectedCountLabel = useMemo(() => t(
        "app.documents.catalog_selected_count",
        {
            defaultValue: "{{count}} prodotti selezionati",
            count: visibleSelectedConfigs.length,
        }
    ), [visibleSelectedConfigs.length, t]);

    useEffect(() => {
        if (!isDesktopLayout) {
            return;
        }
        if (mobileSection !== MOBILE_SECTIONS.SEARCH) {
            setMobileSection(MOBILE_SECTIONS.SEARCH);
        }
    }, [isDesktopLayout, mobileSection]);

    const handleFetchMore = () => {
        setOffset((previous) => previous + DEFAULT_LIMIT);
    };

    const handleSearch = (value) => {
        setOffset(0);
        setQuery(value);
    };

    const toggleProductSelection = (product) => {
        if (!product) {
            return;
        }
        const productIdentifier = resolveProductIdentifier(product);
        setSelectedConfigs((previous) => {
            const existingIndex = previous.findIndex((entry) => {
                const entryIdentifier = resolveProductIdentifier(entry?.product);
                if (productIdentifier !== null) {
                    return entryIdentifier === productIdentifier;
                }
                return entry?.product === product;
            });
            if (existingIndex !== -1) {
                const next = [...previous];
                next.splice(existingIndex, 1);
                return next;
            }
            const baseConfig = buildConfigFromProduct(product, currencySymbol);
            if (!baseConfig) {
                return previous;
            }
            return [...previous, {...baseConfig}];
        });
    };

    const updateConfigByKey = useCallback((cacheKey, updater) => {
        if (!cacheKey) {
            return;
        }
        setSelectedConfigs((previous) => previous.map((entry) => {
            const normalizedEntry = entry ?? {};
            const entryKey = normalizedEntry.cacheKey ?? normalizedEntry.id ?? resolveProductIdentifier(normalizedEntry.product);
            if (entryKey !== cacheKey) {
                return entry;
            }
            const nextEntry = typeof updater === "function"
                ? updater(normalizedEntry)
                : {...normalizedEntry, ...(updater || {})};
            return nextEntry;
        }));
    }, []);

    const handleQuantityChange = useCallback((cacheKey, value) => {
        updateConfigByKey(cacheKey, {quantity: value ?? ""});
    }, [updateConfigByKey]);

    const handleUnitBasePriceChange = useCallback((cacheKey, value) => {
        updateConfigByKey(cacheKey, (entry) => {
            const hasDiscounts = Array.isArray(entry?.unitPriceDiscounts) && entry.unitPriceDiscounts.length > 0;
            const nextValue = value ?? "";
            if (hasDiscounts) {
                return {
                    ...entry,
                    unitBasePrice: nextValue,
                };
            }
            return {
                ...entry,
                unitBasePrice: nextValue,
                unitPrice: nextValue,
            };
        });
    }, [updateConfigByKey]);

    const handleUnitPriceDiscountValuesChange = useCallback((cacheKey, payload) => {
        if (!payload || typeof payload !== "object") {
            return;
        }
        updateConfigByKey(cacheKey, {
            unitBasePrice: payload.list_price ?? "",
            unitPrice: payload.final_price ?? payload.list_price ?? "",
            unitPriceDiscounts: Array.isArray(payload.discount) ? payload.discount : [],
        });
    }, [updateConfigByKey]);

    const handleRemoveConfigByKey = useCallback((cacheKey) => {
        if (!cacheKey) {
            return;
        }
        setSelectedConfigs((previous) => previous.filter((entry) => (entry.cacheKey ?? entry.id) !== cacheKey));
    }, []);

    const handleConfirm = () => {
        if (typeof onConfirm !== "function") {
            return;
        }
        if (!Array.isArray(visibleSelectedConfigs) || visibleSelectedConfigs.length === 0) {
            return;
        }
        if (isSubmitting) {
            return;
        }
        const payload = visibleSelectedConfigs
            .map(mapConfigToPayload)
            .filter((entry) => entry !== null);
        if (payload.length === 0) {
            return;
        }
        onConfirm(payload);
    };

    const renderSearchSection = (isMobileView) => (
        <section className={`${isMobileView ? "flex flex-col gap-4" : "flex h-full flex-col space-y-4 overflow-hidden"}`}>
            <div className={"px-4"}>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">
                    {t("app.documents.catalog_search_label", {defaultValue: "Ricerca prodotti"})}
                </p>
                <h2 className="text-2xl font-semibold text-gray-900">
                    {t("app.documents.catalog_search_title", {defaultValue: "Catalogo aziendale"})}
                </h2>
                <p className="text-sm text-gray-500">
                    {t("app.documents.catalog_search_description", {defaultValue: "Filtra e seleziona rapidamente i prodotti da aggiungere al documento."})}
                </p>
            </div>
            <div className={`${isMobileView ? "flex-1" : "flex-1 overflow-hidden"}`}>
                <ListSection
                    title={t("app.products.products")}
                    columns={columns}
                    totalRows={totalRows}
                    list={products}
                    onSearch={handleSearch}
                    isLoading={(offset > 0 && query.length === 0) ? false : isLoading}
                    mobileRow={CatalogProductMobileRow}
                    desktopRow={CatalogProductDesktopRow}
                    isDynamicSearch
                    onInfiniteScroll={handleFetchMore}
                    activeDynamicFilter
                    isPanelScroll={isDesktopLayout && !isMobileView}
                    panelHeight={isDesktopLayout && !isMobileView ? panelScrollHeight : undefined}
                    extras={{
                        selectedIdSet,
                        selectedProductRefSet,
                        onToggle: toggleProductSelection,
                        company,
                    }}
                    onUpdateSortableRow={() => {}}
                >
                    <DynamicFilterMenu
                        filters={dynamicFilters}
                        isLoading={isLoading && offset === 0}
                        defaultParams={defaultParams}
                        onChange={(filters) => {
                            setActiveDynamicFilter(filters);
                            setOffset(0);
                            setQuery(getQuerySearchParam());
                        }}
                    />
                </ListSection>
            </div>
        </section>
    );

    const renderConfigurationSection = () => (
        <SelectedProductConfigurationPanel
            t={t}
            selectedConfigs={visibleSelectedConfigs}
            selectedCountLabel={selectedCountLabel}
            isSubmitting={isSubmitting}
            onConfirm={handleConfirm}
            onRemove={(cacheKey) => handleRemoveConfigByKey(cacheKey)}
            onQuantityChange={handleQuantityChange}
            onUnitBasePriceChange={handleUnitBasePriceChange}
            onUnitPriceDiscountValuesChange={handleUnitPriceDiscountValuesChange}
        />
    );

    const renderMobileSectionToggle = () => {
        if (isDesktopLayout) {
            return null;
        }
        const configCount = visibleSelectedConfigs.length;
        return (
            <div className="flex flex-col gap-3">
                <div className="flex rounded-full bg-gray-100 p-1 shadow-inner">
                    <button
                        type="button"
                        className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${mobileSection === MOBILE_SECTIONS.SEARCH ? "bg-white text-blue-1000 shadow" : "text-gray-600"}`}
                        onClick={() => setMobileSection(MOBILE_SECTIONS.SEARCH)}
                    >
                        {t("app.documents.catalog_search_label", {defaultValue: "Ricerca"})}
                    </button>
                    <button
                        type="button"
                        className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${mobileSection === MOBILE_SECTIONS.CONFIG ? "bg-white text-blue-1000 shadow" : "text-gray-600"}`}
                        onClick={() => setMobileSection(MOBILE_SECTIONS.CONFIG)}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <span>{t("app.documents.catalog_configuration_title", {defaultValue: "Configurazione"})}</span>
                            {configCount > 0 && (
                                <span className="min-w-[1.5rem] rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-900">
                                    {configCount}
                                </span>
                            )}
                        </div>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <SlidePanel
            isOpen={isOpen}
            setIsOpen={onClose}
            size="max-w-6xl"
            panelZIndexClass={DOCUMENT_PANEL_Z_INDEX_CLASS}
            overlayZIndexClass={DOCUMENT_PANEL_OVERLAY_Z_INDEX_CLASS}
            title={t("app.documents.catalog_add_products", {defaultValue: "Aggiungi prodotti da catalogo"})}
            bg="bg-white"
            hasDivider={false}
            paddingStyle="p-0"
            setClean={() => {}}
        >
            <div className="flex h-full flex-col overflow-hidden">
                {isDesktopLayout ? (
                    <div className="flex h-full flex-col gap-6  pt-6 pb-6 xl:grid xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)] xl:gap-6 xl:overflow-hidden">
                        {renderSearchSection(false)}
                        <div className="xl:h-full">
                            {renderConfigurationSection()}
                        </div>
                    </div>
                ) : (
                    <div className="flex h-full flex-col gap-4 px-4 pb-6 pt-6">
                        {renderMobileSectionToggle()}
                        <div className="flex-1 overflow-auto pb-6">
                            {mobileSection === MOBILE_SECTIONS.SEARCH
                                ? renderSearchSection(true)
                                : renderConfigurationSection()}
                        </div>
                    </div>
                )}
            </div>
        </SlidePanel>
    );
};

const SelectedProductConfigurationPanel = ({
    t,
    selectedConfigs,
    selectedCountLabel,
    isSubmitting,
    onConfirm,
    onQuantityChange,
    onUnitBasePriceChange,
    onUnitPriceDiscountValuesChange,
    onRemove,
}) => {
    const totalNetPrice = useMemo(() => {
        if (!Array.isArray(selectedConfigs)) {
            return 0;
        }
        return selectedConfigs.reduce((total, entry) => {
            const quantity = Number(entry?.quantity ?? 0);
            const unitPrice = Number(entry?.unitPrice ?? entry?.unitBasePrice ?? 0);
            if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
                return total;
            }
            return total + (quantity * unitPrice);
        }, 0);
    }, [selectedConfigs]);

    const currencySymbol = selectedConfigs?.[0]?.currencySymbol ?? "";
    const currencySuffix = currencySymbol ? ` ${currencySymbol}` : "";
    const hasSelection = selectedConfigs.length > 0;
    const confirmDisabled = !hasSelection || isSubmitting;

    return (
        <aside className="flex h-full flex-col rounded-2xl border border-gray-200 bg-gray-50 xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)] xl:overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4">
                <div>
                    <p className="text-base font-semibold text-gray-900">
                        {t("app.documents.catalog_configuration_title", {defaultValue: "Configurazione prodotti"})}
                    </p>
                    <p className="text-xs text-gray-500">
                        {hasSelection
                            ? selectedCountLabel
                            : t("app.documents.catalog_empty_selection", {defaultValue: "Seleziona uno o più prodotti dal catalogo per aggiungerli al documento."})}
                    </p>
                </div>
            </div>
            <div className="flex flex-1 flex-col gap-4 p-4 xl:min-h-0">
                <div className="flex items-center justify-between rounded-lg border border-blue-100 bg-white/80 p-3">
                    <div>
                        <p className="text-sm font-semibold text-gray-900">{selectedCountLabel}</p>
                        <p className="text-xs text-gray-500">
                            {t("app.documents.catalog_config_hint", {defaultValue: "Personalizza quantità e prezzi prima di aggiungere."})}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                            {t("app.documents.catalog_total_preview", {defaultValue: "Totale netto"})}
                        </p>
                        <p className="text-lg font-semibold text-blue-1000">
                            <NumberFormat
                                value={totalNetPrice}
                                displayType="text"
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                suffix={currencySuffix}
                            />
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={confirmDisabled}
                    aria-busy={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-1000 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-70"
                >
                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    <span>{t("app.documents.catalog_add_selected", {defaultValue: "Aggiungi al documento"})}</span>
                </button>
                <div className="flex-1 space-y-4 overflow-y-auto rounded-xl border border-dashed border-gray-300 bg-white/60 xl:min-h-0">
                    <div>
                    {hasSelection ? (
                            <div className="flex flex-col gap-4">
                                {selectedConfigs.map((config) => (
                                    <SelectedProductConfigurationCard
                                        key={config.cacheKey ?? config.id ?? resolveProductIdentifier(config.product)}
                                        t={t}
                                        config={config}
                                        onQuantityChange={onQuantityChange}
                                        onUnitBasePriceChange={onUnitBasePriceChange}
                                        onUnitPriceDiscountValuesChange={onUnitPriceDiscountValuesChange}
                                        onRemove={onRemove}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="flex h-full items-center justify-center px-3 py-12 text-center text-sm text-gray-500">
                                {t("app.documents.catalog_empty_selection", {defaultValue: "Seleziona uno o più prodotti dal catalogo per aggiungerli al documento."})}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </aside>
    );
};

const SelectedProductConfigurationCard = ({
    t,
    config,
    onQuantityChange,
    onUnitBasePriceChange,
    onUnitPriceDiscountValuesChange,
    onRemove,
}) => {
    if (!config) {
        return null;
    }
    const cacheKey = config.cacheKey ?? config.id ?? resolveProductIdentifier(config.product);
    const currencySymbol = config.currencySymbol ?? "";
    const currencySuffix = currencySymbol ? ` ${currencySymbol}` : "";
    const purchasePrice = config.product?.pricing?.purchase_price ?? undefined;
    const productLastPrices = Array.isArray(config.product?.last_prices) ? config.product.last_prices : [];
    const hasPriceHistory = productLastPrices.length > 0;

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <ProductCell product={config.product} isActiveManufacture={false} />
                    {config.product_reference && (
                        <p className="mt-1 text-xs text-gray-500">
                            {config.product_reference}
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => onRemove(cacheKey)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
            <div className="mt-4 space-y-3">
                <InputFormattedNumber
                    label={t("app.documents.catalog_quantity", {defaultValue: "Quantità"})}
                    value={config.quantity ?? ""}
                    decimalScale={2}
                    suffix=" Pz"
                    onChange={(value) => onQuantityChange(cacheKey, value)}
                />
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {t("app.documents.catalog_unit_price", {defaultValue: "Prezzo netto"})}
                    </label>
                    <div className="mt-1 flex items-center gap-3">
                        {hasPriceHistory && (
                            <div className="flex-shrink-0">
                                <PriceHistory prices={productLastPrices} />
                            </div>
                        )}
                        <InputDiscountNumber
                            label={t("app.documents.catalog_unit_price", {defaultValue: "Prezzo netto"})}
                            isShowLabel={false}
                            value={config.unitBasePrice ?? ""}
                            decimalScale={4}
                            onChange={(value) => onUnitBasePriceChange(cacheKey, value)}
                            onChangeValues={(payload) => onUnitPriceDiscountValuesChange(cacheKey, payload)}
                            suffix={currencySuffix}
                            discountList={config.unitPriceDiscounts ?? []}
                            isShowFinalPrice
                            isShowPriceRange={false}
                            symbol={currencySymbol}
                            purchasePrice={purchasePrice}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const CatalogProductMobileRow = ({element, extras}) => {
    const {t} = useTranslation();
    const {selectedIdSet, selectedProductRefSet, onToggle, company} = extras;
    const identifier = resolveProductIdentifier(element);
    const refSet = selectedProductRefSet ?? EMPTY_SET;
    const isSelected = (identifier !== null && selectedIdSet.has(identifier)) || refSet.has(element);

    return (
        <li
            key={element.id}
            className={`flex flex-col gap-3 rounded-xl border border-gray-200 p-4 ${isSelected ? "bg-indigo-50" : "bg-white"}`}
            onClick={() => onToggle(element)}
        >
            <div className="flex items-center gap-3">
                <div className="flex-1">
                    <ProductCell product={element} isActiveManufacture={false} />
                </div>
            </div>
            <div className="flex flex-col gap-2">
                <CardDetail title={t("app.products.manufacture_code")}>
                    {element?.manufacture_reference ?? "-"}
                </CardDetail>
                {company?.product_manage_status && (
                    <CardDetail title={t("app.products.status")}>
                        <StatusBarIcon status={element?.status} />
                    </CardDetail>
                )}
            </div>
        </li>
    );
};

const CatalogProductDesktopRow = ({element, extras}) => {
    const {selectedIdSet, selectedProductRefSet, onToggle, company} = extras;
    const identifier = resolveProductIdentifier(element);
    const refSet = selectedProductRefSet ?? EMPTY_SET;
    const isSelected = (identifier !== null && selectedIdSet.has(identifier)) || refSet.has(element);
    return (
        <tr
            key={element.id}
            className={`cursor-pointer ${isSelected ? "bg-indigo-50" : ""}`}
            onClick={() => onToggle(element)}
        >
            <TableDetail extraClass="whitespace-pre-line">
                <ProductCell product={element} isActiveManufacture={false} />
            </TableDetail>
            <TableDetail>
                <div className="text-sm text-gray-900">
                    {element?.manufacture_reference ?? "-"}
                </div>
                <div className="text-xs text-gray-700">
                    {element?.company_manufacture?.name ?? ""}
                </div>
            </TableDetail>
            {company?.product_manage_status && (
                <TableDetail>
                    <StatusBarIcon status={element?.status} />
                </TableDetail>
            )}
        </tr>
    );
};

SelectedProductConfigurationPanel.propTypes = {
    t: PropTypes.func.isRequired,
    selectedConfigs: PropTypes.arrayOf(PropTypes.object),
    selectedCountLabel: PropTypes.string.isRequired,
    isSubmitting: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    onQuantityChange: PropTypes.func.isRequired,
    onUnitBasePriceChange: PropTypes.func.isRequired,
    onUnitPriceDiscountValuesChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

SelectedProductConfigurationPanel.defaultProps = {
    selectedConfigs: [],
    isSubmitting: false,
};

SelectedProductConfigurationCard.propTypes = {
    t: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    onQuantityChange: PropTypes.func.isRequired,
    onUnitBasePriceChange: PropTypes.func.isRequired,
    onUnitPriceDiscountValuesChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

DocumentBodyCatalogPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSubmitting: PropTypes.bool,
    currencySymbol: PropTypes.string,
};

DocumentBodyCatalogPanel.defaultProps = {
    companyId: null,
    isSubmitting: false,
    currencySymbol: "",
};

CatalogProductMobileRow.propTypes = {
    element: PropTypes.object.isRequired,
    extras: PropTypes.shape({
        selectedIdSet: PropTypes.object.isRequired,
        selectedProductRefSet: PropTypes.object.isRequired,
        onToggle: PropTypes.func.isRequired,
        company: PropTypes.object,
    }).isRequired,
};

CatalogProductDesktopRow.propTypes = {
    element: PropTypes.object.isRequired,
    extras: PropTypes.shape({
        selectedIdSet: PropTypes.object.isRequired,
        selectedProductRefSet: PropTypes.object.isRequired,
        onToggle: PropTypes.func.isRequired,
        company: PropTypes.object,
    }).isRequired,
};

export default DocumentBodyCatalogPanel;
